import { useEffect, useState } from 'react'
import { Link, getRouteApi } from '@tanstack/react-router'
import { AlertTriangle, ArrowRight, CheckCircle2, Clock, Copy, ExternalLink, QrCode } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardBody, CardHeader } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { Skeleton } from '@/components/ui/skeleton'
import { OrderStatusBadge, methodLabel } from '@/components/ui/status'
import { useToast } from '@/components/ui/toast'
import { useOrder, useSimulatePayment } from '@/features/payment/api'
import { formatCountdown, formatDateTime, formatRupiah } from '@/lib/format'
import type { Order } from '@/lib/types'

const route = getRouteApi('/authenticated/checkout/$orderId')

export function CheckoutPage() {
  const { orderId } = route.useParams()
  const { data: order, isLoading } = useOrder(orderId, { poll: true })

  if (isLoading) {
    return (
      <div className="mx-auto w-full max-w-3xl px-4 py-12 sm:px-6">
        <Skeleton className="h-64 w-full rounded-2xl" />
      </div>
    )
  }

  if (!order) {
    return (
      <div className="mx-auto w-full max-w-3xl px-4 py-20 sm:px-6">
        <EmptyState
          className="card"
          title="Tagihan tidak ditemukan"
          description="Tagihan mungkin sudah dibatalkan atau bukan milik akun ini."
          action={
            <Link to="/app/pesanan">
              <Button variant="outline">Lihat pesanan saya</Button>
            </Link>
          }
        />
      </div>
    )
  }

  return (
    <div className="mx-auto w-full max-w-4xl px-4 py-10 sm:px-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-ink sm:text-3xl">
            {order.state === 'paid' ? 'Pembayaran berhasil' : 'Selesaikan pembayaran'}
          </h1>
          <p className="mt-1 text-muted">{order.course.title}</p>
        </div>
        <OrderStatusBadge state={order.state} />
      </header>

      {order.state === 'paid' ? (
        <PaidState order={order} />
      ) : order.state === 'pending' ? (
        <PendingState order={order} />
      ) : (
        <ClosedState order={order} />
      )}
    </div>
  )
}

function PaidState({ order }: { order: Order }) {
  return (
    <Card className="mt-6">
      <CardBody className="flex flex-col items-center py-12 text-center">
        <span className="flex size-16 items-center justify-center rounded-2xl bg-success-soft text-success">
          <CheckCircle2 className="size-8" />
        </span>
        <h2 className="mt-5 text-xl font-extrabold text-ink">Akses kelas sudah terbuka</h2>
        <p className="mt-2 max-w-md text-muted">
          Pembayaran {formatRupiah(order.amount)} terkonfirmasi pada{' '}
          {order.paid_at ? formatDateTime(order.paid_at) : 'beberapa saat lalu'}. Kelas sudah ada di daftar
          belajar Anda.
        </p>

        <div className="mt-7 flex flex-wrap justify-center gap-3">
          <Link to="/app/kelas-saya">
            <Button size="lg">
              Mulai belajar
              <ArrowRight className="size-4" />
            </Button>
          </Link>
          <Link to="/app/pesanan">
            <Button size="lg" variant="outline">
              Lihat pesanan
            </Button>
          </Link>
        </div>
      </CardBody>
    </Card>
  )
}

function ClosedState({ order }: { order: Order }) {
  return (
    <Card className="mt-6">
      <CardBody className="flex flex-col items-center py-12 text-center">
        <span className="flex size-16 items-center justify-center rounded-2xl bg-warning-soft text-warning">
          <AlertTriangle className="size-8" />
        </span>
        <h2 className="mt-5 text-xl font-extrabold text-ink">Tagihan sudah tidak berlaku</h2>
        <p className="mt-2 max-w-md text-muted">
          Tidak ada dana yang terpotong. Anda dapat memesan ulang kelas ini kapan saja dengan harga yang
          berlaku saat itu.
        </p>
        <Link to="/kelas/$slug" params={{ slug: order.course.slug }} className="mt-7">
          <Button size="lg">Pesan ulang kelas</Button>
        </Link>
      </CardBody>
    </Card>
  )
}

function PendingState({ order }: { order: Order }) {
  const instructions = order.payment?.instructions
  const simulate = useSimulatePayment(order.id)
  const toast = useToast()

  return (
    <div className="mt-6 grid gap-6 lg:grid-cols-[1.5fr_1fr]">
      <div className="space-y-6">
        <Countdown order={order} />

        <Card>
          <CardHeader
            title={`Instruksi ${methodLabel[order.method] ?? order.method}`}
            description="Ikuti langkah berikut dari aplikasi pembayaran Anda."
          />
          <CardBody className="space-y-5">
            {instructions?.virtual_account_number ? (
              <CopyRow
                label={instructions.bank_name ?? 'Virtual Account'}
                value={instructions.virtual_account_number}
              />
            ) : null}

            {instructions?.qr_payload ? (
              <div className="flex flex-col items-center rounded-xl border border-line bg-sunken p-6 text-center">
                <span className="flex size-40 items-center justify-center rounded-xl bg-raised">
                  <QrCode className="size-20 text-ink" />
                </span>
                <p className="mt-3 text-xs text-subtle">Kode: {instructions.qr_payload}</p>
              </div>
            ) : null}

            {instructions?.deeplink ? (
              <a href={instructions.deeplink} target="_blank" rel="noreferrer">
                <Button fullWidth variant="secondary">
                  Buka aplikasi dompet digital
                  <ExternalLink className="size-4" />
                </Button>
              </a>
            ) : null}

            {instructions?.redirect_url ? (
              // Halaman pembayaran yang dihosting penyedia menangani seluruh
              // saluran sekaligus, jadi tombolnya adalah tindakan utama di
              // layar ini — bukan pelengkap di bawah petunjuk.
              <a href={instructions.redirect_url} target="_blank" rel="noreferrer">
                <Button fullWidth size="lg">
                  {instructions.snap_token ? 'Bayar sekarang' : 'Lanjut ke halaman kartu'}
                  <ExternalLink className="size-4" />
                </Button>
              </a>
            ) : null}

            {instructions?.steps?.length ? (
              <ol className="space-y-3">
                {instructions.steps.map((step, index) => (
                  <li key={step} className="flex gap-3">
                    <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-brand-soft text-xs font-bold text-brand-ink">
                      {index + 1}
                    </span>
                    <span className="text-sm leading-relaxed text-muted">{step}</span>
                  </li>
                ))}
              </ol>
            ) : null}
          </CardBody>
        </Card>

        {import.meta.env.DEV ? (
          <Card className="border-dashed">
            <CardBody className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="text-sm font-bold text-ink">Mode pengembangan</p>
                <p className="mt-0.5 text-sm text-muted">
                  Picu webhook pembayaran tiruan untuk menguji alur tanpa penyedia sungguhan.
                </p>
              </div>
              <Button
                variant="outline"
                loading={simulate.isPending}
                onClick={() =>
                  simulate.mutate(undefined, {
                    onSuccess: () => toast.success('Pembayaran tersimulasi', 'Akses kelas sudah dibuka.'),
                    onError: () => toast.error('Simulasi gagal'),
                  })
                }
              >
                Simulasikan pembayaran
              </Button>
            </CardBody>
          </Card>
        ) : null}
      </div>

      <aside className="lg:sticky lg:top-24 lg:h-fit">
        <Card>
          <CardHeader title="Ringkasan tagihan" />
          <CardBody className="space-y-4">
            <div className="flex gap-3">
              {order.course.thumbnail_url ? (
                <img
                  src={order.course.thumbnail_url}
                  alt=""
                  className="size-16 shrink-0 rounded-lg object-cover"
                />
              ) : null}
              <div className="min-w-0">
                <p className="clamp-2 text-sm font-bold text-ink">{order.course.title}</p>
                <p className="mt-0.5 text-xs text-muted">{order.course.expert_name}</p>
              </div>
            </div>

            <dl className="space-y-2 border-t border-line pt-4 text-sm">
              <div className="flex justify-between">
                <dt className="text-muted">Metode</dt>
                <dd className="font-semibold text-ink">{methodLabel[order.method] ?? order.method}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted">Batas bayar</dt>
                <dd className="font-semibold text-ink">{formatDateTime(order.expires_at)}</dd>
              </div>
              <div className="flex items-center justify-between border-t border-line pt-2">
                <dt className="font-semibold text-ink">Total</dt>
                <dd className="text-lg font-extrabold text-ink">{formatRupiah(order.amount)}</dd>
              </div>
            </dl>

            <p className="text-xs leading-relaxed text-subtle">
              Halaman ini memeriksa status pembayaran secara otomatis. Setelah dana terkonfirmasi, akses
              kelas terbuka tanpa perlu menyegarkan halaman.
            </p>
          </CardBody>
        </Card>
      </aside>
    </div>
  )
}

/**
 * Hitung mundur memakai sisa waktu yang dihitung server, bukan selisih jam
 * perangkat. Jam ponsel yang meleset beberapa menit tidak boleh membuat
 * pengguna mengira tagihannya sudah hangus.
 */
function Countdown({ order }: { order: Order }) {
  const [remaining, setRemaining] = useState(order.seconds_to_expiry)

  useEffect(() => {
    setRemaining(order.seconds_to_expiry)
  }, [order.seconds_to_expiry])

  useEffect(() => {
    const timer = window.setInterval(() => {
      setRemaining((value) => Math.max(0, value - 1))
    }, 1000)
    return () => window.clearInterval(timer)
  }, [])

  const urgent = remaining > 0 && remaining < 15 * 60

  return (
    <div
      className={`card flex items-center justify-between gap-4 p-5 ${urgent ? 'border-warning/40 bg-warning-soft' : ''}`}
    >
      <div className="flex items-center gap-3">
        <span
          className={`flex size-11 items-center justify-center rounded-xl ${
            urgent ? 'bg-warning/15 text-warning' : 'bg-brand-soft text-brand'
          }`}
        >
          <Clock className="size-5" />
        </span>
        <div>
          <p className="text-sm font-bold text-ink">Selesaikan sebelum waktu habis</p>
          <p className="text-sm text-muted">Setelah itu tagihan otomatis dibatalkan.</p>
        </div>
      </div>

      <p className="text-2xl font-extrabold tabular-nums text-ink" aria-live="off">
        {formatCountdown(remaining)}
      </p>
    </div>
  )
}

function CopyRow({ label, value }: { label: string; value: string }) {
  const toast = useToast()
  const [copied, setCopied] = useState(false)

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      toast.info('Salin manual', 'Peramban menolak akses papan klip.')
    }
  }

  return (
    <div className="rounded-xl border border-line bg-sunken p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-subtle">{label}</p>
      <div className="mt-2 flex items-center justify-between gap-3">
        <p className="font-mono text-xl font-bold tracking-wide text-ink">{value}</p>
        <Button size="sm" variant="outline" onClick={copy}>
          {copied ? <CheckCircle2 className="size-4 text-success" /> : <Copy className="size-4" />}
          {copied ? 'Tersalin' : 'Salin'}
        </Button>
      </div>
    </div>
  )
}
